import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { Button, Divider, Checkbox, InfoSheetPanel, Paragraph, Separator, Row } from '@fs/zion-ui'
import { ControlSplit, NoticeLoading } from '@fs/zion-icon'
import {
  getNestedOrFloatingElementById,
  getPersonIdsForField,
  useSlsStateContext,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import { useR2DispatchContext, useR2StateContext } from '../../../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { R2AppName } from '../../../../../../../shared/services/config'
import PanelHeader from '../../../shared/PanelHeader'
import SharedFieldPersonsCheckboxes from '../selectPersons/SharedFieldPersonsCheckboxes'
import { createReassignHighlightDispatches, getLinkedFields } from './reassignHighlightUtils'

const getPersonsSharingField = (allPersonIdsSharingFieldOnImages, images) => {
  return allPersonIdsSharingFieldOnImages?.map((personId) => getNestedOrFloatingElementById(images, personId))
}

const ReassignHighlight = ({ overlay, handleSave, saving, personName, fieldValue, onDiscard, appName = R2AppName }) => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { closeOverlay } = useOverlayContext()
  const h = useSlsHighlightsContext()
  const { getHighlights, activeFieldInfo } = h

  const shape = {}
  const schema = Yup.object().shape(shape)
  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {},
  })

  // Get linked fields from the new linkedHighlightsFieldInfo
  const linkedFields = useMemo(() => {
    return getLinkedFields(getHighlights, state.images, activeFieldInfo)
  }, [getHighlights, state.images, activeFieldInfo])

  // Use the current field (the one being moved FROM) for display
  const currentFieldLabel = h.activeFieldInfo?.displayLabel || h.activeFieldInfo?.label || 'Current Field'
  const currentFieldValue = h.activeFieldInfo?.fieldValues?.[0]?.origValue?.text || fieldValue || ''

  const description = t(
    'reassign-highlight.description',
    'The highlight is about to move from the assigned field to another:'
  )

  // Find all persons sharing the linked fields
  const allPersonIdsSharingFieldOnImages = useMemo(() => {
    let ids =
      linkedFields?.reduce((acc, field) => {
        const fieldPersonIds = getPersonIdsForField(field, state.images)
        acc = acc.concat(fieldPersonIds)
        return acc
      }, []) || []

    // Add current selected person if not already included
    if (!ids.includes(r2State.selectedPersonId)) {
      ids.unshift(r2State.selectedPersonId)
    }

    // Remove duplicates and sort
    ids = [...new Set(ids)]
    ids.sort((a, b) => {
      if (a === r2State.selectedPersonId) return -1
      if (b === r2State.selectedPersonId) return 1
      return 0
    })
    return ids
  }, [linkedFields, r2State.selectedPersonId, state.images])

  const allPersonsSharingField = useMemo(
    () => getPersonsSharingField(allPersonIdsSharingFieldOnImages, state.images),
    [allPersonIdsSharingFieldOnImages, state.images]
  )

  // Split persons by image - following the pattern from AddHighlightLinkedFields
  const currentArkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === state.currentImageArk)
  const allTheArks = allPersonsSharingField?.reduce((acc, person) => {
    if (!acc[person.partitionKey]) {
      acc[person.partitionKey] = [person]
    } else {
      acc[person.partitionKey].push(person)
    }
    return acc
  }, {})

  const personsSharingFieldOnImage = allTheArks?.[state?.currentImageArk] || []
  // Filter out the selected person from the affected names list
  const affectedPersonsOnImage = personsSharingFieldOnImage.filter((person) => person.id !== r2State.selectedPersonId)

  const allTheArksExceptCurrent = useMemo(() => {
    if (allTheArks) {
      const clone = { ...allTheArks }
      delete clone[state.currentImageArk]
      return clone
    }
    return {}
  }, [allTheArks, state.currentImageArk])

  const { reset, setValue } = methods

  useEffect(() => {
    const formDefaultValues = {}
    if (allPersonIdsSharingFieldOnImages) {
      allPersonIdsSharingFieldOnImages.forEach((personId) => {
        formDefaultValues[personId] = personId === r2State.selectedPersonId
      })
    }
    reset(formDefaultValues)
  }, [allPersonIdsSharingFieldOnImages, r2State.selectedPersonId, reset])

  const handleSubmit = () => {
    createReassignHighlightDispatches({
      highlightsContext: h,
      linkedFields,
      activeFieldInfo: h.activeFieldInfo,
      images: state.images,
      fieldValue,
      handleSave,
      appName,
    })
  }

  const handleBack = () => {
    if (onDiscard) {
      onDiscard()
    } else {
      closeOverlay('editSharedHighlightPage2')
    }
  }

  // Build the panel title
  const panelTitle = personName ? `Edit ${personName}` : 'Edit Highlight'

  const assignedToText = t('reassign-highlight.assigned-to', 'This highlight will be assigned to')

  const affectedNamesText = t(
    'reassign-highlight.affected-names',
    'The highlight will be removed and affect the names below:'
  )

  return (
    <InfoSheetPanel
      {...overlay}
      headingColor="yellow"
      title={panelTitle}
      close={handleBack}
      footer={
        <Row alignX="end" gutters="md">
          <Button testId="panel-back-button" emphasis="low" onClick={handleBack} disabled={saving}>
            {t('reassign-highlight.back', 'Back')}
          </Button>
          <Button
            testId="panel-continue-button"
            emphasis="high"
            form="reassign-highlight-form"
            type="submit"
            disabled={saving}
            Icon={saving ? NoticeLoading : undefined}
          >
            {t('reassign-highlight.update', 'Update')}
          </Button>
        </Row>
      }
    >
      <ZionForm onSubmit={handleSubmit} methods={methods} id="reassign-highlight-form">
        <PanelHeader heading={t('reassign-highlight.heading', 'Reassign the Highlight')} caption={description} />
        <Separator />

        <Paragraph size="sm">
          {assignedToText}
          <Separator size="none" />
          <i>
            {currentFieldLabel}: {currentFieldValue}
          </i>
        </Paragraph>

        <Checkbox checked disabled label={personName} />
        <Separator size="nano" />
        <Separator size="xs" />

        <Paragraph size="sm">{affectedNamesText}</Paragraph>
        <Separator size="xs" />

        {affectedPersonsOnImage.length > 0 && (
          <SharedFieldPersonsCheckboxes
            personsSharingFieldOnImage={affectedPersonsOnImage}
            linkedFieldValue={currentFieldValue}
            isNewPerson={false}
            setValue={setValue}
          />
        )}

        {/* Cross-page persons section - following AddHighlightLinkedFields pattern */}
        {allTheArksExceptCurrent &&
          Object.entries(allTheArksExceptCurrent)
            // This filter keeps us from attempting to load a page that isn't directly adjacent to current
            // This is necessary because we don't load other pages into state, so we don't have their data
            .filter(([ark]) => {
              const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
              return Math.abs(currentArkIndex - arkIndex) === 1
            })
            .map(([ark, persons]) => {
              const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
              const currentPageNumber = arkIndex + 1
              // Filter out the selected person from cross-page persons too
              const affectedPersonsOnOtherPage = persons.filter((person) => person.id !== r2State.selectedPersonId)

              // Only render if there are affected persons on this page
              if (affectedPersonsOnOtherPage.length === 0) return null

              return (
                <React.Fragment key={ark}>
                  <Separator size="xs" />
                  <Divider
                    label={t('reassign-highlight.names-on-page-n', 'Names on Page {pageNumber}', {
                      pageNumber: currentPageNumber,
                    })}
                  />
                  <Button
                    Icon={ControlSplit}
                    onClick={() =>
                      r2Dispatch({
                        type: 'OPEN_SPLIT_SCREEN_TO_PAGE',
                        value: currentPageNumber,
                      })
                    }
                    emphasis="medium"
                  >
                    {t('reassign-highlight.view-in-context', 'View in Context')}
                  </Button>
                  <SharedFieldPersonsCheckboxes
                    personsSharingFieldOnImage={affectedPersonsOnOtherPage}
                    linkedFieldValue={currentFieldValue}
                    isNewPerson={false}
                    setValue={setValue}
                  />
                </React.Fragment>
              )
            })}
      </ZionForm>
    </InfoSheetPanel>
  )
}

export default ReassignHighlight
